import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Building2, Globe } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

export const COMPANY_WEBSITE = 'https://numuw.io';
export const WHATSAPP_USERNAME = 'numuw.io';
export const WHATSAPP_URL = `https://wa.me/${WHATSAPP_USERNAME}`;

const DEFAULT_COLORS = {
  text: '#1e293b',
  textSecondary: '#475569',
  textMuted: '#64748b',
  border: '#e2e8f0',
  surfaceAlt: '#f8fafc',
  indigo: '#4f46e5',
  indigoLight: 'rgba(79, 70, 229, 0.08)',
};

function WhatsAppIcon({ size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M20.52 3.48A11.86 11.86 0 0 0 12.06 0C5.5 0 .16 5.33.16 11.89c0 2.1.55 4.14 1.59 5.95L0 24l6.33-1.66a11.9 11.9 0 0 0 5.72 1.46h.01c6.56 0 11.9-5.34 11.9-11.9 0-3.18-1.24-6.16-3.44-8.42zM12.06 21.8h-.01a9.88 9.88 0 0 1-5.03-1.38l-.36-.21-3.76.99 1-3.66-.24-.38a9.86 9.86 0 0 1-1.51-5.27c0-5.45 4.44-9.89 9.9-9.89 2.64 0 5.12 1.03 6.99 2.9a9.82 9.82 0 0 1 2.89 6.99c0 5.45-4.44 9.9-9.87 9.9zm5.42-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.6.13-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35z" />
    </svg>
  );
}

/**
 * Shared About panel used by the login About modal and Settings → About.
 */
export default function AboutContent({ colors = DEFAULT_COLORS, embedded = false }) {
  const { t, isRTL } = useLanguage();
  const [websiteQr, setWebsiteQr] = useState('');
  const c = { ...DEFAULT_COLORS, ...colors };

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(COMPANY_WEBSITE, { width: 220, margin: 1 })
      .then((url) => { if (!cancelled) setWebsiteQr(url); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Hero */}
      <div style={{
        padding: embedded ? '20px 20px 18px' : '20px 24px',
        background: `linear-gradient(135deg, ${c.indigoLight} 0%, transparent 100%)`,
        borderBottom: `1px solid ${c.border}`,
        borderRadius: embedded ? '12px 12px 0 0' : 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img
            src={process.env.PUBLIC_URL + '/icon.svg'}
            alt={t('settings.aboutAppName')}
            style={{
              width: 56,
              height: 56,
              objectFit: 'contain',
              flexShrink: 0,
            }}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: c.text, letterSpacing: '-0.01em' }}>
              {t('settings.aboutAppName')}
            </div>
            <div style={{ fontSize: 12.5, color: c.textMuted, marginTop: 3 }}>
              {t('settings.aboutTagline')}
            </div>
          </div>
        </div>
        <p style={{
          margin: '14px 0 0',
          fontSize: 13,
          color: c.textSecondary,
          lineHeight: 1.65,
        }}>
          {t('settings.aboutInfo')}
        </p>
      </div>

      {/* Details grid */}
      <div style={{ padding: embedded ? '20px' : '20px 24px 24px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: 20,
          alignItems: 'start',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              background: c.surfaceAlt,
              borderRadius: 10,
              border: `1px solid ${c.border}`,
            }}>
              <Building2 size={16} color={c.indigo} style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: c.textSecondary }}>
                {t('settings.aboutCompany').replace(isRTL ? 'نمو.' : 'Numuw', '').trim()}{' '}
                <span style={{ fontWeight: 700, color: c.text }}>{isRTL ? 'نمو.' : 'Numuw'}</span>
              </span>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              background: c.surfaceAlt,
              borderRadius: 10,
              border: `1px solid ${c.border}`,
            }}>
              <Globe size={16} color={c.indigo} style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: c.textSecondary }}>
                {t('settings.aboutWebsite')}:{' '}
                <a
                  href={COMPANY_WEBSITE}
                  target="_blank"
                  rel="noopener noreferrer"
                  dir="ltr"
                  style={{
                    fontWeight: 600,
                    color: c.indigo,
                    textDecoration: 'none',
                    cursor: 'pointer',
                  }}
                >
                  numuw.io
                </a>
              </span>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              background: c.surfaceAlt,
              borderRadius: 10,
              border: `1px solid ${c.border}`,
            }}>
              <WhatsAppIcon size={16} color={c.indigo} />
              <span style={{ fontSize: 13, color: c.textSecondary }}>
                {t('settings.aboutWhatsapp')}:{' '}
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  dir="ltr"
                  style={{
                    fontWeight: 600,
                    color: c.indigo,
                    textDecoration: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {WHATSAPP_USERNAME}
                </a>
              </span>
            </div>
          </div>

          {websiteQr && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              padding: 12,
              background: c.surfaceAlt,
              borderRadius: 12,
              border: `1px solid ${c.border}`,
            }}>
              <img src={websiteQr} alt="numuw.io" style={{ width: 110, height: 110, display: 'block', borderRadius: 6 }} />
              <span style={{ fontSize: 11, color: c.textMuted, textAlign: 'center', maxWidth: 120, lineHeight: 1.4 }}>
                {t('settings.aboutScanQr')}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
